import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Event as EventIcon,
  CalendarMonth as CalendarMonthIcon,
  MoreVert as MoreVertIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import especialistaService from '../../services/especialistaService';
import especialidadService from '../../services/especialidadService';
import PageHeader from '../../components/common/PageHeader';
import DeleteConfirmDialog from '../../components/common/DeleteConfirmDialog';
import { useAuth } from '../../hooks/useAuth';

const EspecialistasList = () => {
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [especialistas, setEspecialistas] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [especialidadFilter, setEspecialidadFilter] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEspecialista, setSelectedEspecialista] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Verificar si el usuario es administrador
  const isAdmin = user?.rol === 'Administrador';

  // Cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Cargar especialistas
        const especialistasResponse = await especialistaService.getEspecialistas();
        setEspecialistas(especialistasResponse.data || []);

        // Cargar especialidades para el filtro
        const especialidadesResponse = await especialidadService.getEspecialidades();
        setEspecialidades(especialidadesResponse.data || []);
      } catch (error) {
        console.error('Error cargando datos:', error);
        showNotification('Error al cargar los datos', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshKey, showNotification]);

  // Filtrar especialistas según la búsqueda y filtro de especialidad
  const filteredEspecialistas = especialistas.filter(especialista => {
    // Filtrar por búsqueda (nombre, apellidos o DNI)
    const matchesSearch = !searchInput || 
      (especialista.usuario?.nombre?.toLowerCase().includes(searchInput.toLowerCase()) ||
       especialista.usuario?.apellidos?.toLowerCase().includes(searchInput.toLowerCase()) ||
       especialista.dni?.toLowerCase().includes(searchInput.toLowerCase()));
    
    // Filtrar por especialidad
    const matchesEspecialidad = !especialidadFilter || 
      especialista.especialidades?.some(esp => esp.id.toString() === especialidadFilter);
    
    return matchesSearch && matchesEspecialidad;
  });

  // Manejar cambio en el campo de búsqueda
  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  // Cambiar filtro de especialidad
  const handleEspecialidadChange = (event) => {
    setEspecialidadFilter(event.target.value);
  };

  // Abrir menú de acciones
  const handleMenuOpen = (event, especialista) => {
    setAnchorEl(event.currentTarget);
    setSelectedEspecialista(especialista);
  };

  // Cerrar menú de acciones
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Abrir diálogo de confirmación para eliminar
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  // Confirmar eliminación de especialista
  const handleDeleteConfirm = async () => {
    try {
      await especialistaService.deleteEspecialista(selectedEspecialista.id);
      showNotification('Especialista desactivado correctamente', 'success');
      setRefreshKey(old => old + 1); // Refrescar lista
    } catch (error) {
      console.error('Error deleting especialista:', error);
      showNotification('Error al desactivar el especialista', 'error');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader 
        title="Especialistas"
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Especialistas' }
        ]}
        actionButton={
          isAdmin && (
            <Button
              component={RouterLink}
              to="/especialistas/nuevo"
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
            >
              Nuevo Especialista
            </Button>
          )
        }
      />
      
      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Buscar por nombre, apellido o DNI..."
                value={searchInput}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="especialidad-select-label">Filtrar por Especialidad</InputLabel>
                <Select
                  labelId="especialidad-select-label"
                  value={especialidadFilter}
                  label="Filtrar por Especialidad"
                  onChange={handleEspecialidadChange}
                >
                  <MenuItem value="">Todas las especialidades</MenuItem>
                  {especialidades.map((especialidad) => (
                    <MenuItem key={especialidad.id} value={especialidad.id.toString()}>
                      {especialidad.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Lista de especialistas */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" my={5}>
              <CircularProgress />
            </Box>
          ) : filteredEspecialistas.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body1" color="textSecondary">
                No se encontraron especialistas con los filtros aplicados.
              </Typography>
              {isAdmin && (
                <Button
                  component={RouterLink}
                  to="/especialistas/nuevo"
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  sx={{ mt: 2 }}
                >
                  Registrar Nuevo Especialista
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>DNI</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Apellidos</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Especialidades</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEspecialistas.map((especialista) => (
                    <TableRow key={especialista.id}>
                      <TableCell>{especialista.dni}</TableCell>
                      <TableCell>{especialista.usuario?.nombre || 'No disponible'}</TableCell>
                      <TableCell>{especialista.usuario?.apellidos || 'No disponible'}</TableCell>
                      <TableCell>{especialista.usuario?.email || 'No disponible'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {especialista.especialidades?.map((especialidad) => (
                            <Chip
                              key={especialidad.id}
                              label={especialidad.nombre}
                              size="small"
                              sx={{ 
                                backgroundColor: especialidad.color,
                                color: 'white'
                              }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Tooltip title="Ver detalles">
                            <IconButton
                              component={RouterLink}
                              to={`/especialistas/${especialista.id}`}
                              size="small"
                              color="info"
                              sx={{ mr: 1 }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          {isAdmin && (
                            <Tooltip title="Editar">
                              <IconButton
                                component={RouterLink}
                                to={`/especialistas/editar/${especialista.id}`}
                                size="small"
                                color="warning"
                                sx={{ mr: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          <Tooltip title="Ver agenda">
                            <IconButton
                              component={RouterLink}
                              to={`/citas/calendario?especialista=${especialista.id}`}
                              size="small"
                              color="primary"
                              sx={{ mr: 1 }}
                            >
                              <CalendarMonthIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Más acciones">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, especialista)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      
      {/* Menú de acciones adicionales */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem 
          component={RouterLink} 
          to={`/citas/nueva?especialista=${selectedEspecialista?.id}`}
          onClick={handleMenuClose}
        >
          <ListItemIcon>
            <EventIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Agendar Cita" />
        </MenuItem>
        
        {isAdmin && (
          <MenuItem onClick={handleDeleteClick}>
            <ListItemIcon>
              <BlockIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary="Desactivar" primaryTypographyProps={{ color: 'error' }} />
          </MenuItem>
        )}
      </Menu>
      
      {/* Diálogo de confirmación para eliminar */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Desactivar Especialista"
        content={`¿Estás seguro de que deseas desactivar al especialista ${selectedEspecialista?.usuario?.nombre} ${selectedEspecialista?.usuario?.apellidos}? Esta acción no eliminará al especialista de la base de datos, pero lo marcará como inactivo.`}
      />
    </Box>
  );
};

export default EspecialistasList;